import { Grid } from '@mui/material';
import { get, isEmpty, map, orderBy, trim } from 'es-toolkit/compat';
import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { CaseSearchItem } from 'services/searchIndex';
import { useCurrentLabId } from 'utils/useCurrentLab';
import FullSearchAutocomplete from './FullSearchAutocomplete';
import { useFullSearch } from './useFullSearch';
import useSlideSearch from './useSlideSearch';

export const fullSearchKeys = ['caseLabel', 'caseId', 'slides.*.id', 'slides.*.fileName'];
export const unknownStudyId = 'unknown';
export const unknownStudyName = 'unknown';

interface FullSearchProps {
  currentStudyId?: string;
  enabled?: boolean;
  isDrawer?: boolean;
  handleChange?: (event: React.SyntheticEvent<Element, Event>, value: string | CaseSearchItem) => void;
}

const FullSearch: FunctionComponent<React.PropsWithChildren<FullSearchProps>> = ({
  currentStudyId,
  enabled = true,
  isDrawer = false,
  handleChange,
}) => {
  const {
    searchQuery,
    searchInputValue,
    setSearchInputValue,
    filteredOptions,
    searchHasNoResults,
    handleSearch,
    handleApplyOption,
    searchIndexSuccess,
    isSearchIndexLoading,
    isErrorSearchIndex,
  } = useFullSearch({ studyId: currentStudyId, enabled, isDrawer, handleChange });

  const latestSearchInputValueRef = React.useRef(searchInputValue);
  const latestHandleSearchRef = React.useRef(handleSearch);
  const latestHandleChangeRef = React.useRef(handleChange);

  React.useEffect(() => {
    latestSearchInputValueRef.current = searchInputValue;
  }, [searchInputValue]);

  React.useEffect(() => {
    latestHandleSearchRef.current = handleSearch;
  }, [handleSearch]);

  React.useEffect(() => {
    latestHandleChangeRef.current = handleChange;
  }, [handleChange]);

  useEffect(() => {
    if (latestSearchInputValueRef.current !== searchQuery) {
      const nextSearchQuery = searchQuery ?? '';

      setSearchInputValue(nextSearchQuery);
      latestHandleSearchRef.current(nextSearchQuery);

      if (isDrawer) {
        latestHandleChangeRef.current?.(null, nextSearchQuery);
      }
    }
  }, [isDrawer, searchQuery, setSearchInputValue]);

  const { labId } = useCurrentLabId();
  const isStudyIdSpecific = Boolean(currentStudyId);

  const optionsSortedByCurrentStudy = useMemo(() => {
    if (isStudyIdSpecific) {
      return orderBy(filteredOptions, (option) => option.studyId === currentStudyId, 'desc');
    }
    return filteredOptions;
  }, [filteredOptions, currentStudyId]);

  const noResults =
    searchInputValue && trim(searchInputValue) !== '' && Boolean(get(searchHasNoResults, searchInputValue, false));

  const {
    data: externalSlidesSearchResultsData,
    isSuccess: isSuccessOtherResults,
    isInitialLoading: isLoadingOtherResults,
  } = useSlideSearch(searchInputValue, Boolean(noResults));

  const otherSearchResults: CaseSearchItem[] = useMemo(() => {
    if (!isSuccessOtherResults) {
      return [];
    }
    // Keep the current lab's lost and found slides together so the grouped list shows each header once.
    const resultsCurrentLabFirst = orderBy(
      externalSlidesSearchResultsData.results,
      (result) => result.labId === labId,
      'desc'
    );
    return map(resultsCurrentLabFirst, (result): CaseSearchItem => {
      return {
        studyId: unknownStudyId,
        studyName: unknownStudyName,
        caseId: null,
        caseLabel: null,
        slides: [
          {
            id: result.id,
            fileName: result.fileName,
            labId: result.labId,
            labName: result.labName,
            caseId: result.caseId,
            caseLabel: result.caseLabel,
            studyId: result.studyId,
          },
        ],
        isSlideOnly: true,
      };
    });
  }, [isSuccessOtherResults, externalSlidesSearchResultsData, labId]);

  const options = !isEmpty(optionsSortedByCurrentStudy) ? optionsSortedByCurrentStudy : otherSearchResults;

  return (
    <Grid container>
      <Grid item xs>
        <FullSearchAutocomplete
          // Don't pass currentStudyId when using options from other studies
          currentStudyId={!isEmpty(optionsSortedByCurrentStudy) ? currentStudyId : undefined}
          value={searchInputValue}
          options={options}
          setValue={setSearchInputValue}
          onSearch={handleSearch}
          onApplySearch={handleApplyOption}
          onInputChange={handleChange}
          disabled={!searchIndexSuccess}
          isDrawer={isDrawer}
          isLoading={isSearchIndexLoading || isLoadingOtherResults}
          isError={isErrorSearchIndex}
          noResults={noResults}
        />
      </Grid>
    </Grid>
  );
};

export default FullSearch;
